/**
 * "✨ Suggest" — ask the agent for activity ideas, review them as a checkbox
 * list, and add the ticked ones to the trip.
 *
 * The AI never writes: what "Add selected" submits is ordinary definition ops
 * (act.upsert + slot.optsAdd) through the same validated, transactional path
 * a human editor uses. A bad proposal dies in validation, not in the itinerary,
 * and everything lands as ○ Deciding.
 */
import type { ActProposal, ImportProposal, ImportResponse, PinsResponse, SuggestResponse } from "@trip/shared/ai";
import type { DefOp } from "@trip/shared/defops";
import { esc, safeUrl } from "@trip/shared/engine/time";
import { isFixed } from "@trip/shared/types";
import { store } from "../store.js";
import { banner } from "./banner.js";
import { isFree, priceLabel, reservationOf } from "./price.js";
import { showThinkOverlay, hideThinkOverlay } from "./thinkoverlay.js";

const auth = (extra: Record<string, string> = {}) => ({
  ...extra,
  ...(store.key ? { "x-trip-key": store.key } : {}),
});

let aiEnabled: boolean | null = null;

/** The probe's cached answer, synchronously — false until it has resolved.
 * For markup that rebuilds after boot (extra rows) and can't await. */
export function aiKnownEnabled(): boolean {
  return aiEnabled === true;
}

/** One probe per page load; the button renders only when the server has a provider. */
export async function checkAiEnabled(): Promise<boolean> {
  if (aiEnabled !== null) return aiEnabled;
  try {
    const res = await fetch(`/api/trip/${encodeURIComponent(store.tripId)}/ai/status`, { headers: auth() });
    aiEnabled = res.ok && ((await res.json()) as { enabled: boolean }).enabled;
  } catch {
    aiEnabled = false;
  }
  return aiEnabled;
}

let lastProposals: ActProposal[] = [];

export function openAiSuggest(dayId: string) {
  const def = store.def;
  const day = def.days.find((d) => d.id === dayId);
  if (!day) return;
  const slots = day.items
    .map((it, i) => ({ it, i }))
    .filter((x) => !isFixed(x.it)) as { it: { s: string }; i: number }[];
  if (!slots.length) {
    banner("This day has no flexible slots to add options to.", "warn");
    return;
  }

  const modal = document.getElementById("modal")!;
  modal.style.maxWidth = "600px";
  modal.innerHTML = `
    <div class="m-head">
      <div class="m-cat">AI assist</div><h3>✨ Suggest for ${esc(day.dow)} ${esc(day.date)}</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button>
    </div>
    <div class="m-body">
      <form id="ai-form" novalidate>
        <label class="share-field">
          <span>What are you looking for?</span>
          <input id="ai-prompt" type="text" maxlength="500" placeholder="e.g. more seafood options near the Old Town" autocomplete="off">
        </label>
        <div class="share-fields">
          <label class="share-field">
            <span>Add the picks to</span>
            <select id="ai-slot">${slots
              .map((x) => `<option value="${x.i}">${esc(x.it.s)}</option>`)
              .join("")}</select>
          </label>
          <button class="btn-cta field-btn" type="submit" id="ai-go">Suggest</button>
        </div>
      </form>
      <div id="ai-results"></div>
      <div class="dir-note">Ideas appear as soon as they're ready; map pins are verified in the background (a rate limit means about a second per address) and a suggestion can be added with or without one. Nothing is added until you tick it and save, and added activities start as ○ Deciding.</div>
    </div>`;
  document.getElementById("modalbg")!.classList.add("open");

  document.getElementById("ai-form")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    const prompt = (document.getElementById("ai-prompt") as HTMLInputElement).value.trim();
    if (!prompt) return;
    const go = document.getElementById("ai-go") as HTMLButtonElement;
    const results = document.getElementById("ai-results")!;
    go.disabled = true;
    results.innerHTML = `<div class="share-empty">Thinking — usually 20-40s for a handful of ideas…</div>`;
    showThinkOverlay();
    try {
      const res = await fetch(`/api/trip/${encodeURIComponent(store.tripId)}/ai/suggest`, {
        method: "POST",
        headers: auth({ "content-type": "application/json" }),
        body: JSON.stringify({ prompt, dayId, slotIndex: Number((document.getElementById("ai-slot") as HTMLSelectElement).value) }),
      });
      const data = (await res.json()) as SuggestResponse & { error?: string };
      if (!res.ok) {
        results.innerHTML = `<div class="share-result share-error" style="display:block">${esc(data.error ?? "That didn't work.")}</div>`;
        return;
      }
      lastProposals = data.proposals;
      if (!lastProposals.length) {
        results.innerHTML = `<div class="share-empty">No usable suggestions came back — try rewording.</div>`;
        return;
      }
      results.innerHTML = `
        <div class="ai-list">${lastProposals.map((p, idx) => proposalRow(p, idx)).join("")}</div>
        <div class="share-copyrow">
          <button type="button" class="btn-cta" data-action="ai-add" data-day="${esc(dayId)}">Add selected</button>
          <span class="share-warn" id="ai-count">0 selected</span>
          <span style="font-size:11.5px;color:var(--mut)">${data.remainingToday} AI calls left today</span>
        </div>`;
      results.querySelectorAll("input[type=checkbox]").forEach((cb) =>
        cb.addEventListener("change", () => {
          const n = results.querySelectorAll("input[type=checkbox]:checked").length;
          document.getElementById("ai-count")!.textContent = `${n} selected`;
        })
      );
      wireFlyout(results);
      if (data.pinJob) void followPins(data.pinJob);
    } catch {
      results.innerHTML = `<div class="share-result share-error" style="display:block">Couldn't reach the server.</div>`;
    } finally {
      go.disabled = false;
      hideThinkOverlay();
    }
  });
}

/**
 * Poll the background geocoder and patch each row's pin badge as answers
 * arrive. Purely additive: the list is already usable, and a proposal can be
 * ticked and added whether or not its pin has landed.
 */
async function followPins(job: string) {
  const deadline = Date.now() + 90_000;
  while (Date.now() < deadline) {
    // stop if the modal closed or a second search replaced these rows
    if (!document.getElementById("ai-results")?.querySelector(".ai-list")) return;
    await new Promise((r) => setTimeout(r, 1200));
    let data: PinsResponse;
    try {
      const res = await fetch(`/api/trip/${encodeURIComponent(store.tripId)}/ai/pins/${encodeURIComponent(job)}`, {
        headers: auth(),
      });
      if (!res.ok) return;
      data = (await res.json()) as PinsResponse;
    } catch {
      return; // offline or navigating away — the rows keep their "locating" state
    }
    for (const [key, info] of Object.entries(data.pins)) {
      const idx = Number(key);
      const p = lastProposals[idx];
      if (!p || !p.pinPending) continue;
      p.geo = info.geo;
      p.pinNote = info.pinNote;
      p.pinPending = false;
      const slot = document.querySelector(`[data-pin="${idx}"]`);
      if (slot) slot.outerHTML = pinBadge(p, idx);
    }
    if (data.done) {
      // anything the job never reported (expired job, restart) stops spinning
      for (const [idx, p] of lastProposals.entries()) {
        if (!p.pinPending) continue;
        p.pinPending = false;
        p.pinNote = p.pinNote ?? "couldn't verify this address — added without a map pin";
        const slot = document.querySelector(`[data-pin="${idx}"]`);
        if (slot) slot.outerHTML = pinBadge(p, idx);
      }
      return;
    }
  }
}

/** The one bit of a row that changes after render. */
function pinBadge(p: ActProposal, idx: number): string {
  if (p.pinPending) return `<span class="badge b-view ai-locating" data-pin="${idx}">locating…</span>`;
  if (p.geo) return `<span class="badge b-go" data-pin="${idx}" title="verified on the map">📍 pinned</span>`;
  return `<span class="badge b-view" data-pin="${idx}" title="${esc(p.pinNote ?? "")}">no pin</span>`;
}

function proposalRow(p: ActProposal, idx: number): string {
  const a = p.act;
  return `
    <div class="ai-row">
      <label class="ai-main">
        <input type="checkbox" data-idx="${idx}">
        <span class="ai-name">${esc(a.name)}</span>
        ${a.cat ? `<span class="badge b-view">${esc(a.cat)}</span>` : ""}
        ${pinBadge(p, idx)}
        <button type="button" class="btn-det btn-sm ai-detbtn" data-det="${idx}">ⓘ details</button>
      </label>
      <div class="ai-why">${esc(p.why)}</div>
    </div>`;
}

/* ---------------- details flyout (replaces the cramped inline <details>) ---------------- */

let flyoutEl: HTMLElement | null = null;

function closeFlyout() {
  flyoutEl?.remove();
  flyoutEl = null;
}

/**
 * A card-sized flyout over the suggestion list: photo, full notes, address,
 * price/hours, website, and the model's "why". Photo comes from Wikipedia
 * directly when the proposal names an article, otherwise from the server's
 * cached photo resolver.
 */
function showProposalFlyout(p: ActProposal) {
  closeFlyout();
  const a = p.act;
  const web = safeUrl(a.web);
  const free = isFree(a.price, a.badge);
  const el = document.createElement("div");
  el.className = "ai-flyout";
  el.innerHTML = `
    <button type="button" class="m-close ai-flyout-close" aria-label="close">✕</button>
    <div class="ai-flyout-photo" style="display:none"><img alt="" loading="lazy"><span class="ai-flyout-credit"></span></div>
    <div class="ai-flyout-body">
      <div class="m-cat">${esc(a.cat ?? "Suggestion")}</div>
      <h3>${esc(a.name)}</h3>
      <div class="ai-flyout-meta">
        ${free ? `<span class="ap-badge ap-free">FREE</span>` : a.price ? `<span class="ap-price">${esc(priceLabel(a.price, a.cat))}</span>` : ""}
        ${reservationOf(a.badge) === "book" ? `<span class="ap-badge">⚑ Book ahead</span>` : reservationOf(a.badge) === "walkup" ? `<span class="ap-badge ap-walkup">Walk-up</span>` : `<span class="ap-badge ap-unknown">Reservation?</span>`}
        ${a.durs.map((d) => `<span class="ap-chip">${d}h</span>`).join("")}
      </div>
      ${a.hours ? `<div class="ai-flyout-row">🕒 ${esc(a.hours)}</div>` : ""}
      ${a.addr ? `<div class="ai-flyout-row">📍 ${esc(a.addr)}</div>` : ""}
      ${web ? `<div class="ai-flyout-row">🔗 <a href="${web}" target="_blank" rel="noopener">${esc(web.replace(/^https?:\/\/(www\.)?/, "").split("/")[0])}</a></div>` : ""}
      ${a.notes ? `<div class="ai-flyout-notes">${esc(a.notes)}</div>` : ""}
      <div class="ai-why">${esc(p.why)}${p.pinNote ? ` · ${esc(p.pinNote)}` : ""}</div>
    </div>`;
  el.querySelector(".ai-flyout-close")!.addEventListener("click", closeFlyout);
  document.getElementById("modal")!.appendChild(el);
  flyoutEl = el;

  const applyPhoto = (url: string | null, credit: string, page?: string | null) => {
    if (!url || flyoutEl !== el) return;
    const wrap = el.querySelector<HTMLElement>(".ai-flyout-photo")!;
    wrap.querySelector("img")!.src = url;
    const cap = wrap.querySelector<HTMLElement>(".ai-flyout-credit")!;
    const safePage = safeUrl(page ?? undefined);
    cap.innerHTML = safePage ? `Photo · <a href="${safePage}" target="_blank" rel="noopener">${esc(credit)}</a>` : `Photo · ${esc(credit)}`;
    wrap.style.display = "";
  };
  if (a.wiki) {
    fetch("https://en.wikipedia.org/api/rest_v1/page/summary/" + encodeURIComponent(a.wiki.replace(/ /g, "_")))
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => applyPhoto(j?.thumbnail?.source ?? j?.originalimage?.source ?? null, "Wikipedia", j?.content_urls?.desktop?.page))
      .catch(() => {});
  } else {
    const place = a.addr?.split(",").pop()?.trim() ?? "";
    fetch(
      `/api/trip/${encodeURIComponent(store.tripId)}/ai/photo?name=${encodeURIComponent(a.name.split("—")[0].trim())}&place=${encodeURIComponent(place)}`,
      { headers: auth() }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { photo?: { url: string; credit: string; page?: string } | null } | null) => {
        if (j?.photo) applyPhoto(j.photo.url, j.photo.credit, j.photo.page);
      })
      .catch(() => {});
  }
}

/** One listener per results container: opens the flyout for any ⓘ button. */
function wireFlyout(results: HTMLElement) {
  results.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-det]");
    if (b) showProposalFlyout(lastProposals[Number(b.dataset.det)]);
  });
}

/* ---------------- paste-an-itinerary import ---------------- */

let lastImport: ImportProposal[] = [];

const firstFlexIndex = (dayId: string): number => {
  const day = store.def.days.find((d) => d.id === dayId);
  return day ? day.items.findIndex((it) => !isFixed(it)) : -1;
};

export function openAiImport() {
  const modal = document.getElementById("modal")!;
  modal.style.maxWidth = "640px";
  modal.innerHTML = `
    <div class="m-head">
      <div class="m-cat">AI assist</div><h3>Import an itinerary</h3>
      <button class="m-close" data-action="close" aria-label="close">✕</button>
    </div>
    <div class="m-body">
      <form id="ai-imp-form" novalidate>
        <label class="ed-fld"><span>Paste itinerary text</span>
          <textarea id="ai-imp-text" rows="6" maxlength="20000" placeholder="Paste from Wanderlog, an email, a note — anything with places and days in it."></textarea></label>
        <label class="ed-fld"><span>…or choose a PDF</span>
          <input id="ai-imp-file" type="file" accept="application/pdf,.pdf"></label>
        <div id="ai-imp-filestat" class="dir-note" style="display:none"></div>
        <div class="share-copyrow">
          <button class="btn-cta" type="submit" id="ai-imp-go">Extract places</button>
        </div>
      </form>
      <div id="ai-results"></div>
      <div class="dir-note">The pasted text is treated as data, never as instructions. Every extracted place waits for your checkbox, lands as ○ Deciding, and pins only after the map verifies the address.</div>
    </div>`;
  document.getElementById("modalbg")!.classList.add("open");

  /** the last chosen PDF, already read: text layer or rendered page images */
  let pdf: { text: string; images: string[] } | null = null;
  const filestat = () => document.getElementById("ai-imp-filestat")!;

  document.getElementById("ai-imp-file")!.addEventListener("change", async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    pdf = null;
    if (!file) {
      filestat().style.display = "none";
      return;
    }
    filestat().style.display = "";
    try {
      const { extractPdf } = await import("./pdftext.js");
      const ex = await extractPdf(file, (msg) => (filestat().textContent = msg));
      pdf = { text: ex.text, images: ex.images };
      filestat().textContent = ex.images.length
        ? `No text layer (a Wanderlog-style export) — rendered ${ex.renderedPages} of ${ex.pages} pages for the agent to read.`
        : `Read ${ex.text.length.toLocaleString()} characters of text from ${ex.pages} pages.`;
    } catch {
      filestat().textContent = "Couldn't read that PDF — paste its text instead.";
    }
  });

  document.getElementById("ai-imp-form")!.addEventListener("submit", async (e) => {
    e.preventDefault();
    const typed = (document.getElementById("ai-imp-text") as HTMLTextAreaElement).value.trim();
    const text = typed || pdf?.text || "";
    const images = pdf?.images.length ? pdf.images : undefined;
    if (text.length < 40 && !images) return banner("Paste a few lines, or choose a PDF first.", "warn");
    const go = document.getElementById("ai-imp-go") as HTMLButtonElement;
    const results = document.getElementById("ai-results")!;
    go.disabled = true;
    results.innerHTML = `<div class="share-empty">Reading the document — usually 20-40s…</div>`;
    try {
      const res = await fetch(`/api/trip/${encodeURIComponent(store.tripId)}/ai/import`, {
        method: "POST",
        headers: auth({ "content-type": "application/json" }),
        body: JSON.stringify({ text, ...(images ? { images } : {}) }),
      });
      const data = (await res.json()) as ImportResponse & { error?: string };
      if (!res.ok) {
        results.innerHTML = `<div class="share-result share-error" style="display:block">${esc(data.error ?? "That didn't work.")}</div>`;
        return;
      }
      lastImport = data.proposals;
      lastProposals = data.proposals; // followPins patches badges through the shared list
      if (!lastImport.length) {
        results.innerHTML = `<div class="share-empty">No places found in that text.</div>`;
        return;
      }
      // only days that can actually take an option — an all-fixed arrival day
      // in the list would just bounce at add time with a confusing warning
      const dayOpts = (sel: string | null) =>
        store.def.days
          .filter((d) => firstFlexIndex(d.id) !== -1)
          .map((d) => `<option value="${d.id}" ${d.id === sel ? "selected" : ""}>${esc(d.dow)} ${esc(d.date)}</option>`)
          .join("");
      results.innerHTML = `
        <div class="ai-list">${lastImport.map((p, idx) => proposalRow(p, idx)).join("")}</div>
        <div class="ai-impdays">${lastImport
          .map(
            (p, idx) => `<label class="ai-impday"><span>${esc(p.act.name.slice(0, 28))}</span>
              <select data-imp-day="${idx}"><option value="">— pick a day —</option>${dayOpts(p.dayId)}</select></label>`
          )
          .join("")}</div>
        <div class="share-copyrow">
          <button type="button" class="btn-cta" data-action="ai-import-add">Add selected</button>
          <span class="share-warn" id="ai-count">0 selected</span>
          <span style="font-size:11.5px;color:var(--mut)">${data.remainingToday} AI calls left today</span>
        </div>`;
      results.querySelectorAll("input[type=checkbox]").forEach((cb) =>
        cb.addEventListener("change", () => {
          const n = results.querySelectorAll("input[type=checkbox]:checked").length;
          document.getElementById("ai-count")!.textContent = `${n} selected`;
        })
      );
      wireFlyout(results);
      if (data.pinJob) void followPins(data.pinJob);
    } catch {
      results.innerHTML = `<div class="share-result share-error" style="display:block">Couldn't reach the server.</div>`;
    } finally {
      go.disabled = false;
      hideThinkOverlay();
    }
  });
}

/** Submit ticked imports: each into its chosen day's first flexible slot. */
export async function addSelectedImports() {
  const checked = [...document.querySelectorAll<HTMLInputElement>("#ai-results input[type=checkbox]:checked")].map((cb) =>
    Number(cb.dataset.idx)
  );
  if (!checked.length) return banner("Tick the places you want first.", "warn");

  const ops: DefOp[] = [];
  const skipped: string[] = [];
  for (const idx of checked) {
    const p = lastImport[idx];
    if (!p) continue;
    const dayId = document.querySelector<HTMLSelectElement>(`select[data-imp-day="${idx}"]`)?.value ?? "";
    const slot = dayId ? firstFlexIndex(dayId) : -1;
    if (!dayId || slot === -1) {
      skipped.push(p.act.name);
      continue;
    }
    ops.push({ op: "act.upsert", id: p.id, act: p.act, origin: "ai", ...(p.geo ? { geo: p.geo } : {}) });
    ops.push({ op: "slot.optsAdd", dayId, index: slot, actIds: [p.id] });
  }
  if (skipped.length) return banner(`Pick a day for: ${skipped.slice(0, 3).join(", ")}${skipped.length > 3 ? "…" : ""}`, "warn");
  if (!ops.length) return;

  showThinkOverlay();
  try {
    const res = await fetch(`/api/trip/${encodeURIComponent(store.tripId)}/def`, {
      method: "POST",
      headers: auth({ "content-type": "application/json" }),
      body: JSON.stringify({ baseRev: store.rev, ops }),
    });
    const data = (await res.json()) as { error?: string; code?: string };
    if (!res.ok) {
      banner(data.code === "stale" ? "The trip changed under you — reloading." : (data.error ?? "Couldn't add those."), "warn");
      if (data.code === "stale") setTimeout(() => location.reload(), 1200);
      return;
    }
    banner(`Imported ${ops.length / 2} ${ops.length === 2 ? "place" : "places"} — they're in the dropdowns now.`, "ok");
    document.getElementById("modalbg")!.classList.remove("open");
    hideThinkOverlay();
  } catch {
    banner("Couldn't reach the server.", "warn");
    hideThinkOverlay();
  }
}

/** Submit the ticked proposals as definition ops. */
export async function addSelectedProposals(dayId: string) {
  const checked = [...document.querySelectorAll<HTMLInputElement>("#ai-results input[type=checkbox]:checked")].map((cb) =>
    Number(cb.dataset.idx)
  );
  if (!checked.length) {
    banner("Tick the suggestions you want first.", "warn");
    return;
  }
  const slotIndex = Number((document.getElementById("ai-slot") as HTMLSelectElement).value);

  const ops: DefOp[] = [];
  for (const idx of checked) {
    const p = lastProposals[idx];
    if (!p) continue;
    ops.push({ op: "act.upsert", id: p.id, act: p.act, origin: "ai", ...(p.geo ? { geo: p.geo } : {}) });
    ops.push({ op: "slot.optsAdd", dayId, index: slotIndex, actIds: [p.id] });
  }

  showThinkOverlay();
  try {
    const res = await fetch(`/api/trip/${encodeURIComponent(store.tripId)}/def`, {
      method: "POST",
      headers: auth({ "content-type": "application/json" }),
      body: JSON.stringify({ baseRev: store.rev, ops }),
    });
    const data = (await res.json()) as { error?: string; code?: string };
    if (!res.ok) {
      banner(data.code === "stale" ? "The trip changed under you — reloading." : (data.error ?? "Couldn't add those."), "warn");
      if (data.code === "stale") setTimeout(() => location.reload(), 1200);
      return;
    }
    banner(`Added ${checked.length} ${checked.length === 1 ? "activity" : "activities"} — they're in the dropdown now.`, "ok");
    // our own resync broadcast refreshes the page state; close the modal
    document.getElementById("modalbg")!.classList.remove("open");
    hideThinkOverlay();
  } catch {
    banner("Couldn't reach the server.", "warn");
    hideThinkOverlay();
  }
}
